import React, { useRef, useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import apiClient from "utils/apiClient";
import { useTranslation } from "react-i18next";

/* ── Icons ── */
const MenuIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
    <line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);
const BellIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/>
  </svg>
);
const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);
const LogOutIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);
const ChevronDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);
const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const CheckAllIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
    <polyline points="20 6 9 17 4 12"/><polyline points="14 6 8 12"/>
  </svg>
);
const ExternalLinkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
    <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

function Avatar({ name, size = "sm" }) {
  const initials = (name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const colors = ["#4F46E5","#0EA5E9","#10B981","#F59E0B","#EF4444","#8B5CF6"];
  const bg = colors[(name || "U").charCodeAt(0) % colors.length];
  const dim = size === "sm" ? 28 : 36;
  return (
    <div
      className={`lms-avatar ${size === "sm" ? "lms-avatar-sm" : ""}`}
      style={{ background: bg, width: dim, height: dim, fontSize: size === "sm" ? 11 : 13 }}
    >
      {initials}
    </div>
  );
}

function useDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);
  return { open, setOpen, ref };
}

const TYPE_CONFIG = {
  critical: { color: "var(--danger-600,#dc2626)", bg: "var(--danger-50,#fef2f2)", border: "var(--danger-200,#fecaca)", dot: "#dc2626" },
  expired:  { color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe", dot: "#7c3aed" },
  warning:  { color: "var(--warning-700,#b45309)", bg: "var(--warning-50,#fffbeb)", border: "var(--warning-200,#fde68a)", dot: "#d97706" },
  info:     { color: "var(--brand-700,#3730a3)", bg: "var(--brand-50,#eef2ff)", border: "var(--brand-200,#c7d2fe)", dot: "#4f46e5" },
};

const TYPE_LABEL_KEYS = { critical: "Segera!", expired: "Berakhir", warning: "Perhatian", info: "Info" };

const READ_KEY = "lms_notif_read";
const getRead = () => { try { return JSON.parse(localStorage.getItem(READ_KEY) || "[]"); } catch { return []; } };
const setRead = (ids) => { try { localStorage.setItem(READ_KEY, JSON.stringify(ids)); } catch {} };

export default function TopBar({ onToggleSidebar }) {
  const { t } = useTranslation();
  const TYPE_LABEL = { critical: t("Segera!"), expired: t("Berakhir"), warning: t("Perhatian"), info: t("Info") };
  const history = useHistory();
  const { user } = useAuth();
  const userDropdown = useDropdown();
  const notifDropdown = useDropdown();

  const [notifications, setNotifications] = useState([]);
  const [readIds, setReadIds] = useState(getRead);
  const [notifLoading, setNotifLoading] = useState(false);

  const userName = user?.full_name || user?.name || user?.username || "Admin";
  const userEmail = user?.email || "";

  const fetchNotifications = useCallback(async () => {
    setNotifLoading(true);
    try {
      const res = await apiClient.get("/notifications");
      setNotifications(res.data?.data || []);
    } catch {
      // silently fail
    } finally {
      setNotifLoading(false);
    }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // Refetch every 5 minutes
  useEffect(() => {
    const id = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  const unreadCount = notifications.filter((n) => !readIds.includes(n.id)).length;

  const markAllRead = () => {
    const allIds = notifications.map((n) => n.id);
    setReadIds(allIds);
    setRead(allIds);
  };

  const markOneRead = (id) => {
    if (readIds.includes(id)) return;
    const next = [...readIds, id];
    setReadIds(next);
    setRead(next);
  };

  const handleNotifClick = (n) => {
    markOneRead(n.id);
    notifDropdown.setOpen(false);
    history.push(`/admin/tenant-detail/${n.tenant_id}`);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    localStorage.removeItem("user");
    sessionStorage.removeItem("user");
    history.push("/auth/signin");
  };

  const sorted = [...notifications].sort((a, b) => {
    const order = { expired: 0, critical: 1, warning: 2, info: 3 };
    return (order[a.type] ?? 4) - (order[b.type] ?? 4);
  });

  return (
    <header className="lms-topbar">
      <button className="lms-topbar-menu-toggle" onClick={onToggleSidebar} aria-label="Toggle menu">
        <MenuIcon />
      </button>

      <div style={{ flex: 1 }} />

      <div className="lms-topbar-actions">
        {/* Notification bell */}
        <div className="lms-dropdown-wrapper" ref={notifDropdown.ref}>
          <button
            className="lms-topbar-icon-btn"
            aria-label="Notifications"
            style={{ position: "relative" }}
            onClick={() => { notifDropdown.setOpen((o) => !o); if (!notifDropdown.open) fetchNotifications(); }}
          >
            <BellIcon />
            {unreadCount > 0 && (
              <span style={{
                position: "absolute", top: 2, right: 2,
                width: 16, height: 16, borderRadius: "50%",
                background: "var(--danger-500,#ef4444)", color: "#fff",
                fontSize: 9, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "2px solid var(--topbar-bg,#fff)",
              }}>
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {notifDropdown.open && (
            <div
              style={{
                position: "absolute", top: "calc(100% + 8px)", right: 0,
                background: "#fff", border: "1px solid var(--border-subtle)",
                borderRadius: 16, boxShadow: "0 16px 40px -8px rgba(15,23,42,0.18)",
                width: 360, zIndex: 200, overflow: "hidden",
              }}
            >
              {/* Header */}
              <div style={{ padding: "14px 16px 12px", borderBottom: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-strong)" }}>{t("Notifikasi")}</div>
                  {unreadCount > 0 && (
                    <span style={{ background: "var(--danger-500,#ef4444)", color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: 99, padding: "1px 7px" }}>
                      {unreadCount} {t("baru")}
                    </span>
                  )}
                </div>
                {unreadCount > 0 && (
                  <button
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "var(--brand-600)", display: "flex", alignItems: "center", gap: 4, fontWeight: 500 }}
                    onClick={markAllRead}
                  >
                    <CheckAllIcon /> {t("Tandai semua dibaca")}
                  </button>
                )}
              </div>

              {/* Body */}
              <div style={{ maxHeight: 380, overflowY: "auto" }}>
                {notifLoading ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
                    <div className="lms-spinner" style={{ width: 24, height: 24 }} />
                  </div>
                ) : sorted.length === 0 ? (
                  <div style={{ padding: "32px 16px", textAlign: "center" }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>🎉</div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-strong)" }}>{t("Semua langganan aman")}</div>
                    <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 4 }}>{t("Tidak ada notifikasi saat ini")}</div>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {sorted.map((n) => {
                      const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.info;
                      const isRead = readIds.includes(n.id);
                      return (
                        <button
                          key={n.id}
                          onClick={() => handleNotifClick(n)}
                          style={{
                            display: "flex", alignItems: "flex-start", gap: 10,
                            padding: "12px 16px", background: isRead ? "transparent" : "var(--brand-50,#eef2ff)",
                            border: "none", borderBottom: "1px solid var(--border-subtle)",
                            cursor: "pointer", textAlign: "left", width: "100%",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = "var(--slate-50,#f8fafc)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = isRead ? "transparent" : "var(--brand-50,#eef2ff)"; }}
                        >
                          {/* Type dot */}
                          <div style={{ flexShrink: 0, marginTop: 3 }}>
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: cfg.dot, display: "inline-block" }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                              <span style={{ fontSize: 10.5, fontWeight: 700, color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 99, padding: "1px 7px" }}>
                                {TYPE_LABEL[n.type]}
                              </span>
                              {!isRead && (
                                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--brand-500)", flexShrink: 0 }} />
                              )}
                            </div>
                            <div style={{ fontSize: 13, color: "var(--text-base)", lineHeight: 1.45 }}>{n.message}</div>
                            <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                              {t("Berakhir:")} {n.expired_at}
                              <ExternalLinkIcon />
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              {sorted.length > 0 && (
                <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border-subtle)", textAlign: "center" }}>
                  <button
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12.5, color: "var(--brand-600)", fontWeight: 500 }}
                    onClick={() => { notifDropdown.setOpen(false); history.push("/admin/master-tenant"); }}
                  >
                    {t("Lihat semua tenant →")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="lms-topbar-divider" />

        {/* User dropdown */}
        <div className="lms-dropdown-wrapper" ref={userDropdown.ref}>
          <button
            className="lms-topbar-user-btn"
            onClick={() => userDropdown.setOpen((o) => !o)}
          >
            <Avatar name={userName} size="sm" />
            <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-strong)", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {userName}
            </span>
            <ChevronDownIcon />
          </button>

          {userDropdown.open && (
            <div className="lms-dropdown" style={{ minWidth: 200, right: 0 }}>
              <div style={{ padding: "10px 12px 8px" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-strong)" }}>{userName}</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{userEmail}</div>
              </div>
              <div className="lms-dropdown-divider" />
              <button className="lms-dropdown-item" onClick={() => { userDropdown.setOpen(false); history.push("/admin/my-profile"); }}>
                <UserIcon /> {t("My Profile")}
              </button>
              <div className="lms-dropdown-divider" />
              <button
                className="lms-dropdown-item danger"
                onClick={() => { userDropdown.setOpen(false); handleLogout(); }}
              >
                <LogOutIcon /> {t("Sign Out")}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
